import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import BasicLoadingScreen from './BasicLoadingScreen';

interface SetupGuardProps {
  children: React.ReactNode;
}

/**
 * SetupGuard - Ensures users have completed the setup flow before accessing the main app
 * 
 * This guard checks if the user has completed the setup process.
 * If not, it redirects them to the setup page.
 * 
 * This prevents users from bypassing the setup flow by manually changing URLs.
 */
export default function SetupGuard({ children }: SetupGuardProps) {
  const location = useLocation();
  const { user } = useAuth();
  const [hasCompletedSetup, setHasCompletedSetup] = useState<boolean | null>(null);

  useEffect(() => {
    checkSetupStatus();
  }, [user]);

  const checkSetupStatus = async () => {
    if (!user) {
      setHasCompletedSetup(null);
      return;
    }

    try {
      // Check if user is a resource (not a business owner)
      // Resources have 'role': 'resource' in their user_metadata
      const userMetadata = user.user_metadata || {};
      const isResource = userMetadata.role === 'resource';

      if (isResource) {
        console.log('✅ SetupGuard: User is a resource, skipping setup check');
        setHasCompletedSetup(true);
        return;
      }

      // For business owners, check setup status
      const { data: setupStatus, error } = await supabase
        .from('user_setup_status')
        .select('completed')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error checking setup status:', error);
        // If no setup record exists, assume setup not completed
        setHasCompletedSetup(false);
        return;
      }

      const isCompleted = setupStatus?.completed || false;
      
      console.log('🔒 SetupGuard: Checking setup status', {
        userId: user.id,
        setupCompleted: isCompleted
      });

      setHasCompletedSetup(isCompleted);
    } catch (error) {
      console.error('Error in checkSetupStatus:', error);
      setHasCompletedSetup(false);
    }
  };

  // While checking setup status, show loading
  if (hasCompletedSetup === null) {
    return <BasicLoadingScreen message="Checking setup status..." />;
  }

  // If setup not completed, redirect to setup page
  if (!hasCompletedSetup) {
    console.log('❌ SetupGuard: Setup not completed, redirecting to setup page');
    return <Navigate to="/setup" replace state={{ from: location.pathname, userId: user?.id, email: user?.email }} />;
  }

  // Setup completed, render the children
  console.log('✅ SetupGuard: Setup completed, allowing access');
  return <>{children}</>;
}
